import { useCallback, useEffect, useRef, useState } from 'react'

import { prefersReducedMotion } from '@/lib/accessibility'
import {
  computeOverlayBigSunFrame,
  type BigSunBounds,
  type BigSunFrame,
} from '@/lib/big-sun-frame'

export type SunriseOverlayTimings = {
  riseMs: number
  contentMs: number
}

export const RISE_TIMINGS: SunriseOverlayTimings = {
  riseMs: 2000,
  contentMs: 450,
}

/** Mobile nav should feel like a menu, not a cinematic open. */
export const MOBILE_NAV_RISE_TIMINGS: SunriseOverlayTimings = {
  riseMs: 420,
  contentMs: 180,
}

export function getRiseTotalDurationMs(
  timings: SunriseOverlayTimings = RISE_TIMINGS,
): number {
  return timings.riseMs + timings.contentMs
}

function getRiseEnd(timings: SunriseOverlayTimings): number {
  const total = getRiseTotalDurationMs(timings)
  return total <= 0 ? 1 : timings.riseMs / total
}

/** Quick fade-out on close — no reverse sun morph. */
const CLOSE_DURATION_MS = 280

/** Smooth ease-out curve for content reveal / fade. */
function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3
}

export type SunriseOverlayPhase = 'idle' | 'opening' | 'open' | 'closing'

type UseSunriseOverlayTimelineOptions = {
  active: boolean
  timings?: SunriseOverlayTimings
  onOpenComplete?: () => void
  onCloseComplete?: () => void
}

export function useSunriseOverlayTimeline({
  active,
  timings = RISE_TIMINGS,
  onOpenComplete,
  onCloseComplete,
}: UseSunriseOverlayTimelineOptions) {
  const [progress, setProgress] = useState(0)
  const [exitOpacity, setExitOpacity] = useState(1)
  const [phase, setPhase] = useState<SunriseOverlayPhase>('idle')
  const progressRef = useRef(0)
  const exitOpacityRef = useRef(1)
  const rafRef = useRef<number | null>(null)
  const startRef = useRef(0)
  const fromRef = useRef(0)
  const toRef = useRef(0)
  const durationRef = useRef(getRiseTotalDurationMs(timings))
  const timingsRef = useRef(timings)
  const onOpenCompleteRef = useRef(onOpenComplete)
  const onCloseCompleteRef = useRef(onCloseComplete)

  timingsRef.current = timings
  onOpenCompleteRef.current = onOpenComplete
  onCloseCompleteRef.current = onCloseComplete

  const setProgressValue = useCallback((value: number) => {
    progressRef.current = value
    setProgress(value)
  }, [])

  const setExitOpacityValue = useCallback((value: number) => {
    exitOpacityRef.current = value
    setExitOpacity(value)
  }, [])

  const cancelAnimation = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [])

  const animateOpen = useCallback(() => {
    cancelAnimation()
    fromRef.current = progressRef.current
    toRef.current = 1
    durationRef.current = getRiseTotalDurationMs(timingsRef.current)
    startRef.current = performance.now()
    setPhase('opening')
    setExitOpacityValue(1)

    const tick = (now: number) => {
      const elapsed = now - startRef.current
      const rawT =
        durationRef.current <= 0
          ? 1
          : Math.min(1, elapsed / durationRef.current)
      const eased = easeOutCubic(rawT)
      const next = fromRef.current + (toRef.current - fromRef.current) * eased
      setProgressValue(next)

      if (rawT < 1) {
        rafRef.current = requestAnimationFrame(tick)
        return
      }

      rafRef.current = null
      setProgressValue(1)
      setPhase('open')
      onOpenCompleteRef.current?.()
    }

    rafRef.current = requestAnimationFrame(tick)
  }, [cancelAnimation, setExitOpacityValue, setProgressValue])

  const animateCloseFade = useCallback(() => {
    cancelAnimation()
    // Hold the settled sun; only fade the overlay out.
    setProgressValue(1)
    fromRef.current = exitOpacityRef.current
    toRef.current = 0
    durationRef.current = CLOSE_DURATION_MS
    startRef.current = performance.now()
    setPhase('closing')

    const tick = (now: number) => {
      const elapsed = now - startRef.current
      const rawT =
        durationRef.current <= 0
          ? 1
          : Math.min(1, elapsed / durationRef.current)
      const eased = easeOutCubic(rawT)
      const next = fromRef.current + (toRef.current - fromRef.current) * eased
      setExitOpacityValue(next)

      if (rawT < 1) {
        rafRef.current = requestAnimationFrame(tick)
        return
      }

      rafRef.current = null
      setExitOpacityValue(0)
      setPhase('idle')
      onCloseCompleteRef.current?.()
    }

    rafRef.current = requestAnimationFrame(tick)
  }, [cancelAnimation, setExitOpacityValue, setProgressValue])

  const playOpen = useCallback(() => {
    if (prefersReducedMotion()) {
      cancelAnimation()
      setProgressValue(1)
      setExitOpacityValue(1)
      setPhase('open')
      onOpenCompleteRef.current?.()
      return
    }
    animateOpen()
  }, [animateOpen, cancelAnimation, setExitOpacityValue, setProgressValue])

  const playClose = useCallback(() => {
    if (prefersReducedMotion()) {
      cancelAnimation()
      setExitOpacityValue(0)
      setPhase('idle')
      onCloseCompleteRef.current?.()
      return
    }
    animateCloseFade()
  }, [animateCloseFade, cancelAnimation, setExitOpacityValue])

  useEffect(() => {
    if (active) {
      playOpen()
    }
    return cancelAnimation
    // Open animation runs once when the overlay mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only
  }, [active])

  return {
    progress,
    exitOpacity,
    phase,
    playClose,
    isAnimating: phase === 'opening' || phase === 'closing',
  }
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

function range(start: number, end: number, value: number) {
  if (end <= start) return value >= end ? 1 : 0
  return clamp((value - start) / (end - start), 0, 1)
}

export type SunriseOverlayStyles = {
  sunFrame: BigSunFrame
  screenFilled: boolean
  contentOpacity: number
  contentTranslateY: number
  contentBlur: number
}

/**
 * Map timeline progress (0–1) to overlay sun frame + content visibility.
 * 0 → RISE_END: semicircle rises to the top, then reshapes to full width;
 * after that the sun stays full and content fades in.
 * Close uses `exitOpacity` on the root — sun geometry stays settled.
 */
export function sunriseOverlayStyles(
  progress: number,
  bounds: BigSunBounds,
  timings: SunriseOverlayTimings = RISE_TIMINGS,
): SunriseOverlayStyles {
  const riseEnd = getRiseEnd(timings)
  const sunProgress = clamp(range(0, riseEnd, progress), 0, 1)
  const contentT = easeOutCubic(range(riseEnd, 1, progress))
  const screenFilled = progress >= riseEnd

  return {
    sunFrame: computeOverlayBigSunFrame(sunProgress, bounds),
    screenFilled,
    contentOpacity: contentT,
    contentTranslateY: (1 - contentT) * 16,
    contentBlur: (1 - contentT) * 4,
  }
}
